<template>
    <form class="search-bar" @submit.prevent="goToFirst">
        <label class="has-float-label">
            <i class="icon icon-search"/>
            <input
                id="search"
                ref="searchBar"
                v-model="query"
                type="text"
                placeholder=" "
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                spellcheck="false"
                @focus="onFocus"
                @keyup.enter="goToFirst"
                @keyup.esc="handleBlur">
            <span>
                Search
                <i class="mobile">...</i>
                <i class="desktop">for students, courses, or events</i>
            </span>
        </label>

        <div
            v-click-outside="onBlur"
            v-if="show"
            ref="searchResults"
            class="search-results"
            aria-live="assertive">
            <index :app-id="appId" 
                   :api-key="apiKey" 
                   :query="query" 
                   index-name="student">
                <results class="search-results-item">
                    <div slot="header" class="search-results-title">
                        <h3>Students</h3>
                    </div>
                    <router-link
                        slot-scope="{ result }"
                        :to="{ name: 'students.detail', params: { uuid: result.uuid } }">
                        <highlight :result="result" attribute-name="firstName"/>
                        <highlight :result="result" attribute-name="lastName"/>
                        <div v-if="result.email" class="search-right">
                            <highlight :result="result" attribute-name="email"/>
                        </div>
                    </router-link>
                </results>
            </index>
            <index :app-id="appId" 
                   :api-key="apiKey" 
                   :query="query" 
                   index-name="course">
                <results class="search-results-item">
                    <div slot="header" class="search-results-title">
                        <h3>Classes</h3>
                    </div>
                    <router-link
                        slot-scope="{ result }"
                        :to="{ name: 'courses.detail', params: { uuid: result.uuid } }">
                        <highlight :result="result" attribute-name="title"/>
                        <div v-if="result.startTime" class="search-right">
                            {{ [ result.startTime, "HH:mm:ss" ] | moment("h:mma") }}
                            - {{ [ result.endTime, "HH:mm:ss" ] | moment("h:mma") }}
                            <template v-if="daysOfTheWeek(result)">| {{ daysOfTheWeek(result) }}</template>
                        </div>
                    </router-link>
                </results>
            </index>
            <index :app-id="appId" 
                   :api-key="apiKey" 
                   :query="query" 
                   index-name="teams">
                <results class="search-results-item">
                    <div slot="header" class="search-results-title">
                        <h3>Teams</h3>
                    </div>
                    <router-link
                        slot-scope="{ result }"
                        :to="{ name: 'teams.detail', params: { teamId: result.id } }">
                        <highlight :result="result" attribute-name="name"/>
                    </router-link>
                </results>
            </index>
            <index :app-id="appId" 
                   :api-key="apiKey" 
                   :query="query" 
                   index-name="events">
                <results class="search-results-item">
                    <div slot="header" class="search-results-title">
                        <h3>Events</h3>
                    </div>
                    <router-link
                        slot-scope="{ result }"
                        :to="{ name: 'events.detail', params: { uuid: result.uuid } }">
                        <highlight :result="result" attribute-name="title"/>
                    </router-link>
                </results>
            </index>
        </div>
    </form>
</template>

<script>
import { mapState } from 'vuex'
import { Index, Results, Highlight } from 'vue-instantsearch'
import Mousetrap from 'mousetrap'
import { daysOfTheWeek } from '../../utilities'

import ClickOutside from '../../directives/click-outside'

export default {
    name: 'sidebar',
    components: {
        Index,
        Results,
        Highlight
    },
    directives: { ClickOutside },
    data() {
        return {
            query: '',
            results: [],
            focus: false,
            index: 0
        }
    },

    computed: {
        ...mapState({
            apiKey: ({ me }) => me.searchToken,
            appId: ({ params }) => params.algoliaAppId
        }),
        show() {
            return this.hasQuery && this.isInFocus
        },
        hasQuery() {
            return this.query && this.query.length > 0
        },
        isInFocus() {
            return this.focus
        }
    },

    mounted() {
        this.popupItem = this.$refs['searchBar']
        Mousetrap.bind('s', this.handleFocus, 'keyup')
    },

    beforeDestroy() {
        Mousetrap.unbind('s')
    },

    methods: {
        handleFocus() {
            const ref = this.$refs['searchBar']
            ref.setSelectionRange(0, this.query.length)
            ref.focus()
        },
        handleBlur() {
            if ('activeElement' in document) {
                this.onBlur()
                document.activeElement.blur()
            }
        },
        onBlur() {
            this.focus = false
        },
        onFocus() {
            this.focus = true
        },
        goToFirst() {
            this.$nextTick(() => {
                const resultsEl = this.$refs['searchResults']
                if (resultsEl) {
                    const firstEl = resultsEl.querySelector('a:first-of-type')
                    if (firstEl) {
                        firstEl.click()
                    }
                }
            })
        },
        daysOfTheWeek
    },

    watch: {
        $route: function() {
            this.handleBlur()
        }
    }
}
</script>

<style lang="stylus">
@import '../../stylus/variables'

.search-bar
    .has-float-label
        label, > span
            top: 0.5em
            font-size: 65%
            left: 40px
            color: $grey

            .mobile
                display: none

                @media screen and (max-width: 480px)
                    display: inline

            .desktop
                display: inline
                font-style: normal

                @media screen and (max-width: 480px)
                    display: none

        input:placeholder-shown:not(:focus) + *
            top: 1.25em
            color: #000

    .icon
        font-size: 26px
        position: absolute
        left: 0
        top: 8px
        line-height: 50px

    input
        width: 100%
        height: 60px
        background: transparent
        font-size: 16px
        padding: 15px 15px 12px 40px
        outline: none
        border: 0px
        border-bottom: 3px solid transparent
        -webkit-appearance: none

        &:focus
            padding-top: 15px
            border-bottom: 3px solid $medium-blue
            margin-bottom: 0px

    .search-results
        margin-top: 0px
        background: #FFF
        border-radius: 0px 0px 10px 10px
        box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.1), 0px 2px 20px rgba(0, 0, 0, 0.05)
        min-width: 60%
        display: inline-block
        overflow-y: scroll
        max-height: 500px

        @media screen and (max-width: 480px)
            min-width: 100%

        .search-results-title
            border-bottom: 1px solid $border-color
            display: flex
            background: $lighter-grey

            h3
                padding: 15px 20px 10px
                margin: 0px
                text-transform: uppercase
                font-weight: 500
                font-size: 14px
                color: $medium-grey
                letter-spacing: 1px

        .search-results-item
            font-size: 16px

            a
                padding: 15px 20px
                display: block
                color: $darker-grey
                text-decoration: none
                border-bottom: 1px solid $border-color

                .ais-highlight
                    margin-right: 4px

                em
                    font-style: normal
                    background: rgba(17, 188, 246, 0.3)
                    display: inline-block
                    padding: 3px 0px

                .search-right
                    float: right
                    color: #999
                    font-size: 15px
                    display: block
                    padding: 2px 0px

                &:hover, &:focus
                    background: $dark-blue
                    border-bottom: 1px solid $darker-blue
                    border-top: 1px solid $darker-blue
                    margin-top: -1px
                    color: $white
                    cursor: pointer

                    em
                        background: rgba(0, 0, 0, 0.2)

                    .search-right
                        color: $white

                &:focus, &:hover:focus
                    border: 3px solid #FFF !important
                    border-radius: 5px
                    margin-top: 0px
                    padding: 12px 17px

            &:first-child a:hover
                margin-top: 0px
                border-top-width: 0px

        .search-result-list:last-child .search-results-item:last-child
            border-bottom-width: 0px
            border-radius: 0px 0px 10px 10px
</style>
